import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Button, CircularProgress, Alert, Chip, Grid, Card, CardContent, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, IconButton, Switch, FormControlLabel, Divider, FormHelperText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import SensorsIcon from '@mui/icons-material/Sensors';
import BarChartIcon from '@mui/icons-material/BarChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import ReactECharts from 'echarts-for-react';
import { sourceSystemService } from '../services/sourceSystemService';
import { API_BASE_URL } from '../config/api';

const SYSTEM_TYPES = ['sap', 'workiva', 'supplier', 'manual', 'api', 'rtdip'];

const typeColors = {
  sap: 'primary', workiva: 'secondary', supplier: 'warning', manual: 'default', api: 'info', rtdip: 'success'
};

function SourceSystemManager() {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Register dialog
  const [registerOpen, setRegisterOpen] = useState(false);
  const [form, setForm] = useState({
    system_name: '', system_type: 'manual', description: '',
    webhook_url: '', elm_is_sor_default: true,
    // RTDIP-specific fields
    rtdip_connector_url: '', rtdip_api_url: '', rtdip_auth_type: 'api_key',
    default_poll_interval: 300
  });

  // Edit dialog (connection_config + basic fields)
  const [editOpen, setEditOpen] = useState(false);
  const [editingSystemId, setEditingSystemId] = useState(null);
  const [editForm, setEditForm] = useState({
    system_name: '', system_type: 'manual', description: '',
    webhook_url: '', elm_is_sor_default: true,
    connection_config: {},
    sap_connector_url: '',
    rtdip_connector_url: '', rtdip_api_url: '', default_poll_interval: 300
  });

  // Stats
  const [selectedStats, setSelectedStats] = useState(null);

  // RTDIP Sensor Config
  const [sensorDialogOpen, setSensorDialogOpen] = useState(false);
  const [sensorDialogSystem, setSensorDialogSystem] = useState(null);
  const [sensorConfigs, setSensorConfigs] = useState([]);
  const [sensorLoading, setSensorLoading] = useState(false);
  const [sensorFormOpen, setSensorFormOpen] = useState(false);
  const [sensorForm, setSensorForm] = useState({
    sensor_id: '', sensor_name: '', sensor_type: 'energy_meter',
    source_unit: '', recording_method: 'measured',
    poll_interval_seconds: 300, aggregation_method: 'sum',
    facility_id: '', emission_activity_id: '', equipment_id: '',
    emission_scope_type_id: '', emission_component_id: '',
    platform_type: 'fixed',
    detection_capabilities: {}
  });
  const [editingSensorId, setEditingSensorId] = useState(null);

  // OFP entity lookup lists
  const [ofpFacilities, setOfpFacilities] = useState([]);
  const [ofpActivities, setOfpActivities] = useState([]);
  const [ofpEquipment, setOfpEquipment] = useState([]);
  const [ofpEmissionComponents, setOfpEmissionComponents] = useState([]);

  // Timeline chart dialog
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineSystem, setTimelineSystem] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  useEffect(() => { loadSystems(); }, []);

  const loadSystems = async () => {
    try {
      setLoading(true);
      const data = await sourceSystemService.list();
      setSystems(Array.isArray(data) ? data : data.systems || []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load systems', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    try {
      const payload = {
        system_name: form.system_name,
        system_type: form.system_type,
        description: form.description,
        webhook_url: form.webhook_url,
        elm_is_sor_default: form.elm_is_sor_default
      };

      // Add RTDIP-specific connection config
      if (form.system_type === 'rtdip') {
        payload.connection_config = {
          rtdip_connector_url: form.rtdip_connector_url,
          rtdip_api_url: form.rtdip_api_url,
          default_poll_interval: form.default_poll_interval
        };
        payload.auth_type = form.rtdip_auth_type;
      }

      await sourceSystemService.register(payload);
      setSnackbar({ open: true, message: `System "${form.system_name}" registered`, severity: 'success' });
      setRegisterOpen(false);
      setForm({
        system_name: '', system_type: 'manual', description: '', webhook_url: '', elm_is_sor_default: true,
        rtdip_connector_url: '', rtdip_api_url: '', rtdip_auth_type: 'api_key', default_poll_interval: 300
      });
      loadSystems();
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Registration failed', severity: 'error' });
    }
  };

  const handleOpenEdit = (s) => {
    const cc = s.connection_config || {};
    setEditingSystemId(s.source_system_id);
    setEditForm({
      system_name: s.system_name || '',
      system_type: s.system_type || 'manual',
      description: s.description || '',
      webhook_url: s.webhook_url || '',
      elm_is_sor_default: s.elm_is_sor_default !== false,
      connection_config: cc,
      sap_connector_url: cc.sap_connector_url || '',
      rtdip_connector_url: cc.rtdip_connector_url || '',
      rtdip_api_url: cc.rtdip_api_url || '',
      default_poll_interval: cc.default_poll_interval || 300
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Start from the existing config so we never drop keys we don't surface.
      const connection_config = { ...(editForm.connection_config || {}) };
      if (editForm.system_type === 'sap') {
        if (editForm.sap_connector_url) connection_config.sap_connector_url = editForm.sap_connector_url;
        else delete connection_config.sap_connector_url;
      } else if (editForm.system_type === 'rtdip') {
        connection_config.rtdip_connector_url = editForm.rtdip_connector_url;
        connection_config.rtdip_api_url = editForm.rtdip_api_url;
        connection_config.default_poll_interval = editForm.default_poll_interval;
      }
      const payload = {
        system_name: editForm.system_name,
        description: editForm.description,
        webhook_url: editForm.webhook_url,
        elm_is_sor_default: editForm.elm_is_sor_default,
        connection_config
      };
      await sourceSystemService.update(editingSystemId, payload);
      setSnackbar({ open: true, message: 'Source system updated', severity: 'success' });
      setEditOpen(false);
      loadSystems();
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Update failed', severity: 'error' });
    }
  };

  const handleSyncNow = async (systemId) => {
    setSnackbar({ open: true, message: 'SAP sync started — extracting & ingesting all flows…', severity: 'info' });
    try {
      const r = await sourceSystemService.triggerSync(systemId);
      const parts = Object.entries(r.by_document_type || {})
        .map(([k, v]) => `${k}: ${v.processed}${v.held_for_review ? ` (+${v.held_for_review} held)` : ''}`)
        .join(', ');
      setSnackbar({
        open: true,
        message: `SAP sync complete — ${r.total_processed} recorded${parts ? ` · ${parts}` : ''}`,
        severity: 'success'
      });
      loadSystems();
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({
        open: true,
        message: `Sync failed: ${(typeof errMsg === 'string' ? errMsg : errMsg?.message) || err.message}`,
        severity: 'error'
      });
    }
  };

  const handleTestConnection = async (systemId) => {
    try {
      const result = await sourceSystemService.testConnection(systemId);
      const isSuccess = result.status === 'connected' || result.status === 'ok';
      setSnackbar({
        open: true,
        message: isSuccess ? 'Connection successful' : `Connection failed: ${result.message || result.error || 'unknown'}`,
        severity: isSuccess ? 'success' : 'error'
      });
      loadSystems();
    } catch (err) {
      setSnackbar({ open: true, message: 'Connection test failed', severity: 'error' });
    }
  };

  const handleDeactivate = async (systemId) => {
    try {
      await sourceSystemService.deactivate(systemId);
      setSnackbar({ open: true, message: 'System deactivated', severity: 'success' });
      loadSystems();
    } catch (err) {
      setSnackbar({ open: true, message: 'Deactivation failed', severity: 'error' });
    }
  };

  const handleLoadStats = async (system) => {
    try {
      const stats = await sourceSystemService.getStatistics(system.source_system_id);
      setSelectedStats({ ...stats, system_name: system.system_name });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load statistics', severity: 'error' });
    }
  };

  const handleOpenTimeline = async (system) => {
    setTimelineSystem(system);
    setTimelineOpen(true);
    setTimelineLoading(true);
    try {
      const data = await sourceSystemService.getSensorHistory(system.source_system_id, 24);
      setTimelineData(data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load sensor history', severity: 'error' });
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleOpenSensors = async (system) => {
    setSensorDialogSystem(system);
    setSensorDialogOpen(true);
    setSensorLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/rtdip-sensors/${system.source_system_id}`);
      const data = await resp.json();
      setSensorConfigs(Array.isArray(data) ? data : []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load sensor configs', severity: 'error' });
      setSensorConfigs([]);
    } finally {
      setSensorLoading(false);
    }
  };

  const reloadSensorConfigs = async () => {
    if (!sensorDialogSystem) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/rtdip-sensors/${sensorDialogSystem.source_system_id}`);
      const data = await resp.json();
      setSensorConfigs(Array.isArray(data) ? data : []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reload sensor configs', severity: 'error' });
    }
  };

  const handleAddSensor = async () => {
    setEditingSensorId(null);
    setSensorForm({
      sensor_id: '', sensor_name: '', sensor_type: 'energy_meter',
      source_unit: '', recording_method: 'measured',
      poll_interval_seconds: 300, aggregation_method: 'sum',
      facility_id: '', emission_activity_id: '', equipment_id: '',
      emission_scope_type_id: '', emission_component_id: '',
      platform_type: 'fixed',
      detection_capabilities: {}
    });
    await loadOfpEntities();
    setSensorFormOpen(true);
  };

  const handleEditSensor = async (sensor) => {
    setEditingSensorId(sensor.sensor_config_id);
    setSensorForm({
      sensor_id: sensor.sensor_id,
      sensor_name: sensor.sensor_name || '',
      sensor_type: sensor.sensor_type || 'energy_meter',
      source_unit: sensor.source_unit || '',
      recording_method: sensor.recording_method || 'measured',
      poll_interval_seconds: sensor.poll_interval_seconds || 300,
      aggregation_method: sensor.aggregation_method || 'sum',
      facility_id: sensor.facility_id || '',
      emission_activity_id: sensor.emission_activity_id || '',
      equipment_id: sensor.equipment_id || '',
      emission_scope_type_id: sensor.emission_scope_type_id || '',
      emission_component_id: sensor.emission_component_id || '',
      platform_type: sensor.platform_type || 'fixed',
      detection_capabilities: sensor.detection_capabilities || {}
    });
    await loadOfpEntities();
    setSensorFormOpen(true);
  };

  const loadOfpEntities = async () => {
    const apiUrl = API_BASE_URL;
    try {
      const [facResp, actResp, eqResp, ecResp] = await Promise.all([
        fetch(`${apiUrl}/api/ofp/facilities`),
        fetch(`${apiUrl}/api/ofp/emission-activities`),
        fetch(`${apiUrl}/api/ofp/equipment`),
        fetch(`${apiUrl}/api/ofp/emission-components`)
      ]);
      const [fac, act, eq, ec] = await Promise.all([
        facResp.json(), actResp.json(), eqResp.json(), ecResp.json()
      ]);
      setOfpFacilities(Array.isArray(fac) ? fac : []);
      setOfpActivities(Array.isArray(act) ? act : []);
      setOfpEquipment(Array.isArray(eq) ? eq : []);
      setOfpEmissionComponents(Array.isArray(ec) ? ec : []);
    } catch (err) {
      console.warn('Could not load OFP entities:', err.message);
    }
  };

  const handleSaveSensor = async () => {
    const apiUrl = API_BASE_URL;
    try {
      const payload = {
        ...sensorForm,
        source_system_id: sensorDialogSystem.source_system_id
      };

      let resp;
      if (editingSensorId) {
        resp = await fetch(`${apiUrl}/api/rtdip-sensors/${editingSensorId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        resp = await fetch(`${apiUrl}/api/rtdip-sensors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || 'Failed to save sensor');
      }

      setSnackbar({ open: true, message: editingSensorId ? 'Sensor updated' : 'Sensor registered', severity: 'success' });
      setSensorFormOpen(false);
      reloadSensorConfigs();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleDeleteSensor = async (sensorConfigId) => {
    if (!window.confirm('Deactivate this sensor?')) return;
    const apiUrl = API_BASE_URL;
    try {
      const resp = await fetch(`${apiUrl}/api/rtdip-sensors/${sensorConfigId}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error('Failed to deactivate sensor');
      setSnackbar({ open: true, message: 'Sensor deactivated', severity: 'success' });
      reloadSensorConfigs();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>Source Systems</Typography>
          <Typography variant="body1" color="text.secondary">
            Manage external system registrations, connectivity, and NC correction routing
          </Typography>
        </Box>
        <Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRegisterOpen(true)} sx={{ mr: 1 }}>
            Register System
          </Button>
          <IconButton onClick={loadSystems}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {SYSTEM_TYPES.map(type => {
          const count = systems.filter(s => s.system_type === type && s.is_active !== false).length;
          return (
            <Grid item xs={6} sm={2.4} key={type}>
              <Card elevation={1}><CardContent sx={{ textAlign: 'center' }}>
                <Chip label={type.toUpperCase()} size="small" color={typeColors[type] || 'default'} sx={{ mb: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{count}</Typography>
              </CardContent></Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Systems Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Registered Systems ({systems.length})</Typography>
        {systems.length === 0 ? (
          <Alert severity="info">No source systems registered. Register one to start ingesting data.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>ELM is SoR</TableCell>
                <TableCell>Webhook</TableCell>
                <TableCell>Last Tested</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {systems.map((s) => (
                <TableRow key={s.source_system_id} hover>
                  <TableCell>{s.system_name}</TableCell>
                  <TableCell><Chip label={s.system_type} size="small" color={typeColors[s.system_type] || 'default'} /></TableCell>
                  <TableCell>
                    <Chip label={s.elm_is_sor_default ? 'Yes' : 'No'} size="small"
                      color={s.elm_is_sor_default ? 'primary' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {s.webhook_url || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {s.last_connection_test ? new Date(s.last_connection_test).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    <Chip label={s.is_active !== false ? 'Active' : 'Inactive'} size="small"
                      color={s.is_active !== false ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleTestConnection(s.source_system_id)}
                      title="Test Connection"><NetworkCheckIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleOpenEdit(s)}
                      title="Edit Configuration"><EditIcon fontSize="small" /></IconButton>
                    {s.system_type === 'sap' && (
                      <IconButton size="small" onClick={() => handleSyncNow(s.source_system_id)}
                        title="Sync Now (extract & ingest all SAP flows)"><SyncIcon fontSize="small" /></IconButton>
                    )}
                    {s.system_type === 'rtdip' && (
                      <>
                        <IconButton size="small" onClick={() => handleOpenSensors(s)}
                          title="Sensor Configs"><SensorsIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleOpenTimeline(s)} title="View Timeline">
                          <TimelineIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    <IconButton size="small" onClick={() => handleLoadStats(s)} title="View Statistics">
                      <BarChartIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeactivate(s.source_system_id)}
                      title="Deactivate"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Stats Card */}
      {selectedStats && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>Statistics: {selectedStats.system_name}</Typography>
          <Grid container spacing={2}>
            {/* Total sensors for RTDIP */}
            {selectedStats.total_sensors !== undefined && (
              <Grid item>
                <Chip label={`Total Sensors: ${selectedStats.total_sensors}`} color="primary" variant="outlined" />
              </Grid>
            )}
            {/* Status counts */}
            {selectedStats.by_status && Object.entries(selectedStats.by_status).map(([status, count]) => (
              <Grid item key={status}>
                <Chip label={`${status.replace(/_/g, ' ')}: ${count}`} variant="outlined" />
              </Grid>
            ))}
            {/* Recording method for RTDIP */}
            {selectedStats.by_recording_method && Object.entries(selectedStats.by_recording_method).map(([method, count]) => (
              <Grid item key={method}>
                <Chip label={`${method}: ${count}`} color="secondary" variant="outlined" />
              </Grid>
            ))}
            {/* Total readings for RTDIP */}
            {selectedStats.total_readings !== undefined && (
              <Grid item>
                <Chip label={`Readings: ${selectedStats.total_readings}`} color="success" variant="outlined" />
              </Grid>
            )}
            {/* Last reading time */}
            {selectedStats.last_reading_at && (
              <Grid item>
                <Chip label={`Last: ${new Date(selectedStats.last_reading_at).toLocaleTimeString()}`} variant="outlined" />
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Register Dialog */}
      <Dialog open={registerOpen} onClose={() => setRegisterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Register Source System</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="System Name" value={form.system_name}
                onChange={(e) => setForm({ ...form, system_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>System Type</InputLabel>
                <Select value={form.system_type} label="System Type"
                  onChange={(e) => setForm({ ...form, system_type: e.target.value })}>
                  {SYSTEM_TYPES.map(t => <MenuItem key={t} value={t}>{t.toUpperCase()}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel control={
                <Switch checked={form.elm_is_sor_default}
                  onChange={(e) => setForm({ ...form, elm_is_sor_default: e.target.checked })} />
              } label="ELM is SoR (default)" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Webhook URL (for NC corrections)" placeholder="https://..."
                value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} />
            </Grid>
            {form.system_type === 'rtdip' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}><Chip label="RTDIP Configuration" size="small" /></Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="RTDIP Connector URL" placeholder="http://localhost:8088"
                    helperText="URL of the rtdip-connector microservice"
                    value={form.rtdip_connector_url}
                    onChange={(e) => setForm({ ...form, rtdip_connector_url: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="RTDIP Platform API URL" placeholder="https://rtdip.example.com"
                    helperText="RTDIP platform endpoint for sensor data"
                    value={form.rtdip_api_url}
                    onChange={(e) => setForm({ ...form, rtdip_api_url: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Auth Type</InputLabel>
                    <Select value={form.rtdip_auth_type} label="Auth Type"
                      onChange={(e) => setForm({ ...form, rtdip_auth_type: e.target.value })}>
                      <MenuItem value="api_key">API Key</MenuItem>
                      <MenuItem value="oauth2">OAuth 2.0</MenuItem>
                      <MenuItem value="service_account">Service Account</MenuItem>
                      <MenuItem value="none">None</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Default Poll Interval (seconds)" type="number"
                    value={form.default_poll_interval}
                    onChange={(e) => setForm({ ...form, default_poll_interval: parseInt(e.target.value) || 300 })}
                    inputProps={{ min: 10, max: 86400 }} />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRegister} disabled={!form.system_name}>Register</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Source System</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="System Name" value={editForm.system_name}
                onChange={(e) => setEditForm({ ...editForm, system_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="System Type" value={(editForm.system_type || '').toUpperCase()} disabled
                helperText="Type cannot be changed" />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel control={
                <Switch checked={editForm.elm_is_sor_default}
                  onChange={(e) => setEditForm({ ...editForm, elm_is_sor_default: e.target.checked })} />
              } label="ELM is SoR (default)" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={2} value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Webhook URL (for NC corrections)" placeholder="https://..."
                value={editForm.webhook_url} onChange={(e) => setEditForm({ ...editForm, webhook_url: e.target.value })} />
            </Grid>
            {editForm.system_type === 'sap' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}><Chip label="SAP Connection" size="small" /></Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="SAP Connector URL"
                    placeholder="https://sap-connector-716850034167.europe-west4.run.app"
                    helperText="Cloud Run URL of the sap-connector. Used by Test Connection (a 401/403 from the private service still counts as reachable)."
                    value={editForm.sap_connector_url}
                    onChange={(e) => setEditForm({ ...editForm, sap_connector_url: e.target.value })} />
                </Grid>
              </>
            )}
            {editForm.system_type === 'rtdip' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}><Chip label="RTDIP Configuration" size="small" /></Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="RTDIP Connector URL" placeholder="http://localhost:8088"
                    value={editForm.rtdip_connector_url}
                    onChange={(e) => setEditForm({ ...editForm, rtdip_connector_url: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="RTDIP Platform API URL" placeholder="https://rtdip.example.com"
                    value={editForm.rtdip_api_url}
                    onChange={(e) => setEditForm({ ...editForm, rtdip_api_url: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Default Poll Interval (seconds)" type="number"
                    value={editForm.default_poll_interval}
                    onChange={(e) => setEditForm({ ...editForm, default_poll_interval: parseInt(e.target.value) || 300 })}
                    inputProps={{ min: 10, max: 86400 }} />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={!editForm.system_name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* RTDIP Sensor Configuration Dialog */}
      <Dialog open={sensorDialogOpen} onClose={() => setSensorDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>RTDIP Sensors: {sensorDialogSystem?.system_name}</span>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddSensor}>
            Add Sensor
          </Button>
        </DialogTitle>
        <DialogContent>
          {sensorLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : sensorConfigs.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No sensors configured. Click "Add Sensor" to register a sensor.
            </Alert>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Sensor ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Units</TableCell>
                  <TableCell>Poll (s)</TableCell>
                  <TableCell>Last Reading</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sensorConfigs.map((sc) => (
                  <TableRow key={sc.sensor_config_id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{sc.sensor_id}</TableCell>
                    <TableCell>{sc.sensor_name || '-'}</TableCell>
                    <TableCell>
                      <Chip label={sc.sensor_type} size="small"
                        color={sc.sensor_type === 'cems' ? 'error' : sc.sensor_type === 'energy_meter' ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={sc.recording_method || 'measured'} size="small" variant="outlined"
                        color="success" />
                    </TableCell>
                    <TableCell>{sc.source_unit || '-'}</TableCell>
                    <TableCell>{sc.poll_interval_seconds}</TableCell>
                    <TableCell>
                      {sc.last_reading_at
                        ? `${sc.last_reading_value} @ ${new Date(sc.last_reading_at).toLocaleString()}`
                        : 'No data yet'}
                    </TableCell>
                    <TableCell>
                      <Chip label={sc.is_active ? 'Active' : 'Inactive'} size="small"
                        color={sc.is_active ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditSensor(sc)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDeleteSensor(sc.sensor_config_id)} title="Deactivate"><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!sensorLoading && sensorConfigs.length > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                OFP Mapping: {sensorConfigs.filter(s => s.emission_activity_id).length} sensors linked to emission activities,{' '}
                {sensorConfigs.filter(s => s.facility_id).length} linked to facilities,{' '}
                {sensorConfigs.filter(s => s.equipment_id).length} linked to equipment
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSensorDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sensor Add/Edit Form Dialog */}
      <Dialog open={sensorFormOpen} onClose={() => setSensorFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSensorId ? 'Edit Sensor' : 'Register New Sensor'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Sensor ID" value={sensorForm.sensor_id}
                disabled={!!editingSensorId}
                onChange={(e) => setSensorForm({ ...sensorForm, sensor_id: e.target.value })}
                helperText={editingSensorId ? 'Cannot change sensor ID' : 'Unique identifier for this sensor'} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Sensor Name" value={sensorForm.sensor_name}
                onChange={(e) => setSensorForm({ ...sensorForm, sensor_name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Sensor Type</InputLabel>
                <Select value={sensorForm.sensor_type} label="Sensor Type"
                  onChange={(e) => setSensorForm({ ...sensorForm, sensor_type: e.target.value })}>
                  <MenuItem value="cems">CEMS (Stack Emissions)</MenuItem>
                  <MenuItem value="energy_meter">Energy Meter</MenuItem>
                  <MenuItem value="flow_meter">Flow Meter</MenuItem>
                  <MenuItem value="temperature">Temperature</MenuItem>
                  <MenuItem value="ogi">OGI (Optical Gas Imaging)</MenuItem>
                  <MenuItem value="spectrometer">Spectrometer</MenuItem>
                  <MenuItem value="tdlas">TDLAS (Tunable Diode Laser)</MenuItem>
                  <MenuItem value="lidar">LiDAR</MenuItem>
                  <MenuItem value="electrochemical">Electrochemical</MenuItem>
                  <MenuItem value="pid">PID (Photoionization)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Source Unit" value={sensorForm.source_unit}
                placeholder="e.g., kWh, kg/hr, ppm"
                helperText="Native unit from this sensor (e.g. kg/hr for CEMS, ppm for OGI)"
                onChange={(e) => setSensorForm({ ...sensorForm, source_unit: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Platform Type</InputLabel>
                <Select value={sensorForm.platform_type} label="Platform Type"
                  onChange={(e) => setSensorForm({ ...sensorForm, platform_type: e.target.value })}>
                  <MenuItem value="fixed">Fixed (Stationary CEMS/Meter)</MenuItem>
                  <MenuItem value="handheld">Handheld</MenuItem>
                  <MenuItem value="drone">Drone (UAV)</MenuItem>
                  <MenuItem value="satellite">Satellite</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Recording Method</InputLabel>
                <Select value={sensorForm.recording_method} label="Recording Method"
                  onChange={(e) => setSensorForm({ ...sensorForm, recording_method: e.target.value })}>
                  <MenuItem value="measured">Measured (direct sensor reading)</MenuItem>
                  <MenuItem value="calculated">Calculated (via emission factor)</MenuItem>
                </Select>
                <FormHelperText>How the emission value is determined</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Poll Interval (seconds)" type="number"
                value={sensorForm.poll_interval_seconds}
                onChange={(e) => setSensorForm({ ...sensorForm, poll_interval_seconds: parseInt(e.target.value) || 300 })}
                inputProps={{ min: 10, max: 86400 }} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Aggregation</InputLabel>
                <Select value={sensorForm.aggregation_method} label="Aggregation"
                  onChange={(e) => setSensorForm({ ...sensorForm, aggregation_method: e.target.value })}>
                  <MenuItem value="sum">Sum</MenuItem>
                  <MenuItem value="avg">Average</MenuItem>
                  <MenuItem value="max">Maximum</MenuItem>
                  <MenuItem value="last">Last Value</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* OFP Mapping Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="OFP Entity Mapping" size="small" color="primary" /></Divider>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Facility</InputLabel>
                <Select value={sensorForm.facility_id} label="Facility"
                  onChange={(e) => setSensorForm({ ...sensorForm, facility_id: e.target.value })}>
                  <MenuItem value=""><em>None (not mapped)</em></MenuItem>
                  {ofpFacilities.map(f => (
                    <MenuItem key={f.facility_id} value={f.facility_id}>
                      {f.name}{f.organization_name ? ` (${f.organization_name})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Emission Activity</InputLabel>
                <Select value={sensorForm.emission_activity_id} label="Emission Activity"
                  onChange={(e) => setSensorForm({ ...sensorForm, emission_activity_id: e.target.value })}>
                  <MenuItem value=""><em>None (not mapped)</em></MenuItem>
                  {ofpActivities.map(a => (
                    <MenuItem key={a.emission_activity_id} value={a.emission_activity_id}>
                      {a.name}{a.emission_scope_type_id ? ` [${a.emission_scope_type_id}]` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Equipment</InputLabel>
                <Select value={sensorForm.equipment_id} label="Equipment"
                  onChange={(e) => setSensorForm({ ...sensorForm, equipment_id: e.target.value })}>
                  <MenuItem value=""><em>None (not mapped)</em></MenuItem>
                  {ofpEquipment.map(e => (
                    <MenuItem key={e.equipment_id} value={e.equipment_id}>{e.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Emission Component</InputLabel>
                <Select value={sensorForm.emission_component_id} label="Emission Component"
                  onChange={(e) => setSensorForm({ ...sensorForm, emission_component_id: e.target.value })}>
                  <MenuItem value=""><em>None (auto-detect)</em></MenuItem>
                  {ofpEmissionComponents.map(ec => (
                    <MenuItem key={ec.emission_component_id} value={ec.emission_component_id}>
                      {ec.name}{ec.symbol_code ? ` (${ec.symbol_code})` : ''}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>GHG component this sensor measures (e.g., CO2, CH4, N2O)</FormHelperText>
              </FormControl>
            </Grid>

            {/* Detection Capabilities - common fields */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="Detection Capabilities" size="small" color="secondary" /></Divider>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Min Detection Limit (ppm)" type="number"
                value={sensorForm.detection_capabilities.min_detection_ppm || ''}
                onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, min_detection_ppm: parseFloat(e.target.value) || undefined } })}
                helperText="Minimum detectable concentration"
                inputProps={{ step: 0.1 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Max Range (ppm)" type="number"
                value={sensorForm.detection_capabilities.max_range_ppm || ''}
                onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, max_range_ppm: parseFloat(e.target.value) || undefined } })}
                helperText="Maximum measurable concentration"
                inputProps={{ step: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Accuracy (%)" type="number"
                value={sensorForm.detection_capabilities.accuracy_percent || ''}
                onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, accuracy_percent: parseFloat(e.target.value) || undefined } })}
                helperText="Measurement accuracy percentage"
                inputProps={{ step: 0.1, min: 0, max: 100 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Response Time (s)" type="number"
                value={sensorForm.detection_capabilities.response_time_s || ''}
                onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, response_time_s: parseFloat(e.target.value) || undefined } })}
                helperText="Sensor response time in seconds"
                inputProps={{ step: 0.1 }} />
            </Grid>

            {/* Handheld-specific fields */}
            {sensorForm.platform_type === 'handheld' && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth label="Detection Distance (m)" type="number"
                    value={sensorForm.detection_capabilities.detection_distance_m || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, detection_distance_m: parseFloat(e.target.value) || undefined } })}
                    helperText="Max distance for leak detection"
                    inputProps={{ step: 0.5 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Field of View (deg)" type="number"
                    value={sensorForm.detection_capabilities.field_of_view_deg || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, field_of_view_deg: parseFloat(e.target.value) || undefined } })}
                    helperText="Camera / sensor field of view"
                    inputProps={{ step: 1, min: 0, max: 360 }} />
                </Grid>
              </>
            )}

            {/* Drone-specific fields */}
            {sensorForm.platform_type === 'drone' && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth label="Flight Altitude (m)" type="number"
                    value={sensorForm.detection_capabilities.flight_altitude_m || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, flight_altitude_m: parseFloat(e.target.value) || undefined } })}
                    helperText="Typical survey flight altitude"
                    inputProps={{ step: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Spatial Resolution (m)" type="number"
                    value={sensorForm.detection_capabilities.spatial_resolution_m || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, spatial_resolution_m: parseFloat(e.target.value) || undefined } })}
                    helperText="Ground-level spatial resolution"
                    inputProps={{ step: 0.1 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Swath Width (m)" type="number"
                    value={sensorForm.detection_capabilities.swath_width_m || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, swath_width_m: parseFloat(e.target.value) || undefined } })}
                    helperText="Ground coverage width per pass"
                    inputProps={{ step: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Max Flight Time (min)" type="number"
                    value={sensorForm.detection_capabilities.max_flight_time_min || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, max_flight_time_min: parseFloat(e.target.value) || undefined } })}
                    helperText="Maximum endurance per flight"
                    inputProps={{ step: 1 }} />
                </Grid>
              </>
            )}

            {/* Satellite-specific fields */}
            {sensorForm.platform_type === 'satellite' && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth label="Spatial Resolution (m)" type="number"
                    value={sensorForm.detection_capabilities.spatial_resolution_m || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, spatial_resolution_m: parseFloat(e.target.value) || undefined } })}
                    helperText="Ground pixel resolution"
                    inputProps={{ step: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Swath Width (km)" type="number"
                    value={sensorForm.detection_capabilities.swath_width_km || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, swath_width_km: parseFloat(e.target.value) || undefined } })}
                    helperText="Ground coverage width per orbit pass"
                    inputProps={{ step: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Revisit Time (days)" type="number"
                    value={sensorForm.detection_capabilities.revisit_time_days || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, revisit_time_days: parseFloat(e.target.value) || undefined } })}
                    helperText="Days between repeat observations"
                    inputProps={{ step: 0.5 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Orbit Altitude (km)" type="number"
                    value={sensorForm.detection_capabilities.orbit_altitude_km || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, orbit_altitude_km: parseFloat(e.target.value) || undefined } })}
                    helperText="Satellite orbital altitude"
                    inputProps={{ step: 1 }} />
                </Grid>
              </>
            )}

            {/* Fixed-specific fields */}
            {sensorForm.platform_type === 'fixed' && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth label="Stack Height (m)" type="number"
                    value={sensorForm.detection_capabilities.stack_height_m || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, stack_height_m: parseFloat(e.target.value) || undefined } })}
                    helperText="Height of emission stack / vent"
                    inputProps={{ step: 0.5 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Flow Rate Range (m3/hr)" type="number"
                    value={sensorForm.detection_capabilities.max_flow_rate_m3hr || ''}
                    onChange={(e) => setSensorForm({ ...sensorForm, detection_capabilities: { ...sensorForm.detection_capabilities, max_flow_rate_m3hr: parseFloat(e.target.value) || undefined } })}
                    helperText="Maximum measurable flow rate"
                    inputProps={{ step: 1 }} />
                </Grid>
              </>
            )}

            {(ofpFacilities.length === 0 && ofpActivities.length === 0) && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  No OFP entities found. Populate the open_footprint database with facilities, emission activities, and equipment to enable full OFP mapping.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSensorFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSensor} disabled={!sensorForm.sensor_id}>
            {editingSensorId ? 'Update' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timeline Chart Dialog */}
      <Dialog open={timelineOpen} onClose={() => setTimelineOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Sensor Timeline: {timelineSystem?.system_name}</DialogTitle>
        <DialogContent>
          {timelineLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : timelineData && Object.keys(timelineData.readings || {}).length > 0 ? (
            <Box sx={{ height: 400 }}>
              <ReactECharts
                option={{
                  title: { text: 'CO2e Emissions (kg) - Last 24 Hours', left: 'center' },
                  tooltip: { trigger: 'axis' },
                  legend: { data: Object.keys(timelineData?.readings ?? {}), bottom: 0 },
                  grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
                  xAxis: {
                    type: 'time',
                    boundaryGap: false
                  },
                  yAxis: {
                    type: 'value',
                    name: 'CO2e (kg)'
                  },
                  series: Object.entries(timelineData?.readings ?? {}).map(([sensorName, readings]) => ({
                    name: sensorName,
                    type: 'line',
                    smooth: true,
                    data: readings.map(r => [r.timestamp, r.co2e_kg]).sort((a, b) => new Date(a[0]) - new Date(b[0]))
                  }))
                }}
                style={{ height: '100%', width: '100%' }}
              />
            </Box>
          ) : (
            <Alert severity="info">No sensor readings available for the last 24 hours.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimelineOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default SourceSystemManager;
